import { zodResolver } from '@hookform/resolvers/zod';
import {
  CardProps,
  Divider,
  IconButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/joy';
import { PencilSimple, X } from '@phosphor-icons/react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { z } from 'zod';

import { GCM } from '~/assets/utils/cipher';
import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog';
import { Label } from '~/components/ui/fieldset';
import { Field } from '~/components/ui/form';
import {
  EditorControls,
  RichTextEditor,
  RichTextEditorContent,
} from '~/components/ui/rich-text-editor';
import { useTenantSlug } from '~/features/tenant/helpers';
import useDecryption from '~/hooks/cryptography/decryptText';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { useAppSelector } from '~/hooks/global/typedReduxHooks';
import { ReportService } from '~/services/api/reports/endpoints';
import { useWrappedMutation } from '~/services/api/rest-api';
import { getErrorMessage } from '~/services/api/rest-api-error';
import { setReport } from '~/slices/reportDataSlice';

const gcm = new GCM();

const schema = z.object({
  description: z.string().min(8, 'Description must not be empty.'),
  reference_id: z.string(),
});
type FormFields = z.infer<typeof schema>;

export default function UpdateTextBoxButton() {
  const [openDialog, setOpenDialog] = useState(false);
  const { description, referenceId, isDescriptionEditable } = useAppSelector(
    (state) => state.reportData.report,
  );
  if (!isDescriptionEditable) return null;

  return (
    <Dialog open={openDialog} onOpenChange={setOpenDialog}>
      <DialogTrigger asChild>
        <Button
          color='neutral'
          variant='soft'
          startDecorator={<PencilSimple />}
        >
          <FormattedMessage defaultMessage='Upate Description' />
        </Button>
      </DialogTrigger>
      <DialogContent size='5xl'>
        <DialogTitle asChild>
          <div className='flex justify-between items-center'>
            <Typography level='h4'>
              <FormattedMessage defaultMessage='Editing report details' />
            </Typography>
            <DialogClose asChild>
              <IconButton>
                <X />
              </IconButton>
            </DialogClose>
          </div>
        </DialogTitle>
        <UpdateTextBox
          defaultValues={{
            description: description,
            reference_id: referenceId,
          }}
          setOpenDialog={setOpenDialog}
        />
      </DialogContent>
    </Dialog>
  );
}

const UpdateTextBox = ({
  defaultValues,
  setOpenDialog,
}: {
  defaultValues: Partial<FormFields>;
  setOpenDialog: (isOpen: boolean) => void;
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const slug = useTenantSlug();
  const { decryptText } = useDecryption();
  const { createToastNotification } = useToastNotificationContext();
  const reportDetails = useAppSelector((state) => state.reportData.report);

  const form = useForm<FormFields>({
    defaultValues: {
      ...defaultValues,
    },
    resolver: zodResolver(schema),
  });

  const updateDescription = useWrappedMutation(
    ReportService.endpoints.reportActionUpdate.useMutation(),
  );

  const onSubmit = form.handleSubmit(async (data) => {
    await updateDescription.mutateAsync(
      {
        report_reference: data.reference_id,
        payload: {
          description_text: gcm.encrypt({ plainText: data.description }),
        },
        slug: slug,
      },
      {
        async onSuccess(res) {
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Description updated successfully',
            }),
            'success',
          );
          const descriptionText = await decryptText(res.description.description);
          dispatch(
            setReport({
              ...reportDetails,
              description: descriptionText,
              isDescriptionEditable: false,
            }),
          );
          setOpenDialog(false);
          form.reset();
        },
        onError(error) {
          createToastNotification(getErrorMessage(error), 'error');
        },
      },
    );
  });

  return (
    <form onSubmit={onSubmit}>
      <div className='flex flex-col gap-2'>
        <Field size='lg' control={form.control} field='description'>
          <Label>
            {intl.formatMessage({
              defaultMessage: 'Details',
            })}
          </Label>
          <DescriptionField />
        </Field>
        {/* Buttons Box */}
        <div className='flex justify-end mt-2 gap-2'>
          <Button
            variant='plain'
            color='neutral'
            disabled={updateDescription.state.isLoading}
            onClick={() => {
              form.reset();
              setOpenDialog(false);
            }}
          >
            <FormattedMessage defaultMessage='Cancel' />
          </Button>
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant='solid'
                color='secondary'
                shape='pill'
                loading={updateDescription.state.isLoading}
              >
                <FormattedMessage defaultMessage='Save' />
              </Button>
            </DialogTrigger>
            <DialogContent
              asChild
              onInteractOutside={(e) =>
                updateDescription.state.isLoading && e.preventDefault()
              }
            >
              <div className='flex flex-col gap-2'>
                <DialogTitle>
                  <FormattedMessage defaultMessage='Are you sure you want to save these changes?' />
                </DialogTitle>
                <Typography>
                  <FormattedMessage defaultMessage='you can only edit this description once.' />
                </Typography>
                <div className='flex justify-end items-center gap-2'>
                  <DialogClose asChild>
                    <Button variant='plain' color='neutral'>
                      <FormattedMessage defaultMessage='Cancel' />
                    </Button>
                  </DialogClose>
                  <Button
                    type='submit'
                    color='secondary'
                    loading={updateDescription.state.isLoading}
                    onClick={onSubmit}
                  >
                    <FormattedMessage defaultMessage='Save' />
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </form>
  );
};

function DescriptionField({
  variant = 'outlined',
}: Pick<CardProps, 'variant'>) {
  return (
    <RichTextEditor>
      <Card size='sm' color='neutral' variant={variant}>
        <RichTextEditorContent />
        <Divider />
        <div className='flex items-center w-full gap-2'>
          <EditorControls.Formatting />
          <Divider orientation='vertical' />
          <ToggleButtonGroup spacing={0.5} variant='plain' size='sm'>
            <EditorControls.Bold />
            <EditorControls.Underline />
            <EditorControls.Italic />
          </ToggleButtonGroup>
        </div>
      </Card>
    </RichTextEditor>
  );
}
